/**
 * CoachBSpeak — reusable "hear Coach B read this" button.
 *
 * Drop-in component: pass `text` and it renders a small compact button.
 * On click:
 *   1. POSTs the text to `/api/school/voices/preview`
 *   2. Plays the returned MP3 inline (auto-plays)
 *   3. Falls back gracefully to a "voice unavailable" pill if the platform
 *      is in mock mode (no ELEVENLABS_API_KEY yet).
 *
 * Used in:
 *   • Sport IQ course modules (BasketballIQCourseModule, BasketballIQTerm)
 *   • L'Chaim daily lessons (LChaimCompanion, LChaimDashboard)
 *   • My School dashboard counselor greeting
 *   • Anywhere else you want Coach B narration
 */
import React, { useEffect, useRef, useState } from 'react';
import { Volume2, Loader2, Pause, Play } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

/**
 * @param {string} text            The line(s) for Coach B to speak (≤500 chars, will be trimmed).
 * @param {string} label           Optional button label ("Hear Coach B" default).
 * @param {string} variant         "compact" (default) | "full" (larger CTA button).
 * @param {string} accent          Hex color for the button accent (default orange #FF5722).
 * @param {string} testId          data-testid prefix.
 */
const CoachBSpeak = ({ text, label = 'Hear Coach B', variant = 'compact', accent = '#FF5722', testId = 'coach-b-speak' }) => {
  const [busy, setBusy]   = useState(false);
  const [audioUrl, setAudio] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // When the text prop changes (e.g. student navigates from Day 1 → Day 2 in
  // L'Chaim, or from one Basketball IQ term to the next), invalidate any
  // cached audio so the next click regenerates from the new text.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setAudio(null);
    setPlaying(false);
  }, [text]);

  const speak = async () => {
    if (busy) return;
    const clean = (text || '').trim().slice(0, 500);
    if (!clean) { toast.error('Nothing for Coach to say'); return; }

    // If we already generated audio for this text, just toggle playback
    if (audioUrl && audioRef.current) {
      if (audioRef.current.paused) audioRef.current.play();
      else audioRef.current.pause();
      return;
    }

    setBusy(true);
    try {
      const r = await api.post('/school/voices/preview', { text: clean }, { headers: authHeaders() });
      const b = r.data?.briefing || {};
      if (!b.audio_base64) {
        // Mock mode — no API key. Fall back to browser TTS so the student still hears SOMETHING.
        if ('speechSynthesis' in window) {
          const u = new SpeechSynthesisUtterance(clean);
          u.rate = 0.95;
          window.speechSynthesis.speak(u);
          toast.info('Coach B unavailable — using browser voice');
        } else {
          toast.error('Voice unavailable on this browser');
        }
        return;
      }
      const url = b.audio_url || `data:audio/mpeg;base64,${b.audio_base64}`;
      setAudio(url);
      // Wait a tick so the <audio> element mounts, then play.
      setTimeout(() => audioRef.current?.play?.().catch(() => {}), 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Voice unavailable');
    } finally { setBusy(false); }
  };

  const isCompact = variant === 'compact';

  return (
    <span className="inline-flex items-center gap-2" data-testid={testId}>
      <button
        onClick={speak}
        disabled={busy}
        className={
          isCompact
            ? 'inline-flex items-center gap-1.5 border-2 hover:opacity-80 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest transition-opacity disabled:opacity-60'
            : 'inline-flex items-center gap-2 text-black hover:opacity-90 px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.2em] transition-opacity disabled:opacity-60'
        }
        style={
          isCompact
            ? { color: accent, borderColor: accent, backgroundColor: 'transparent' }
            : { backgroundColor: accent, color: '#08080a' }
        }
        title="Coach B reads this aloud"
        data-testid={`${testId}-btn`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" />
          : playing ? <Pause className="w-3 h-3" />
          : audioUrl ? <Play className="w-3 h-3" />
          : <Volume2 className="w-3 h-3" />}
        <span>{busy ? 'Loading…' : playing ? 'Pause' : audioUrl ? 'Play' : label}</span>
      </button>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="hidden"
          data-testid={`${testId}-audio`}
        />
      )}
      {/* Live waveform — 4 bars pulsing while Coach B is speaking */}
      {playing && (
        <span
          className="inline-flex items-end gap-[3px] h-5 ml-1"
          aria-hidden="true"
          data-testid={`${testId}-waveform`}
        >
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="coach-b-bar"
              style={{
                backgroundColor: accent,
                width: '3px',
                display: 'inline-block',
                animation: `coachBBar 0.9s ease-in-out ${i * 0.12}s infinite`,
                borderRadius: '1px',
                transformOrigin: 'bottom',
              }}
            />
          ))}
        </span>
      )}
      {/* Keyframes injected once */}
      <style>{`
        @keyframes coachBBar {
          0%, 100% { height: 30%; opacity: 0.55; }
          50%      { height: 100%; opacity: 1; }
        }
      `}</style>
    </span>
  );
};

export default CoachBSpeak;
